
import { useEffect, useRef, useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const marketplaces = [
  {
    name: 'Trendyol',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/20/Trendyol_online_shopping_company_logo.png/600px-Trendyol_online_shopping_company_logo.png',
    url: 'https://www.trendyol.com/magaza/vitasso-m-917498',
    bgGradient: 'from-orange-50 to-orange-100/50',
    borderColor: 'border-orange-200',
    hoverBorder: 'hover:border-orange-400',
    hoverShadow: 'hover:shadow-orange-200/60',
    accentColor: 'text-orange-600',
    badgeBg: 'bg-orange-500',
    iconBg: 'bg-orange-100',
    description: 'Trendyol mağazamızdan güvenle alışveriş yapın',
    rating: '4.9',
    reviews: '2.400+',
    shipping: 'Süper Hızlı Teslimat',
    shippingIcon: 'ri-rocket-2-line',
  },
  {
    name: 'Hepsiburada',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b2/Hepsiburada_logo_official.svg/600px-Hepsiburada_logo_official.svg.png',
    url: 'https://www.hepsiburada.com/magaza/vitasso',
    bgGradient: 'from-amber-50 to-yellow-100/50',
    borderColor: 'border-amber-200',
    hoverBorder: 'hover:border-amber-400',
    hoverShadow: 'hover:shadow-amber-200/60',
    accentColor: 'text-amber-600',
    badgeBg: 'bg-amber-500',
    iconBg: 'bg-amber-100',
    description: 'Hepsiburada\'da Vitasso ürünlerini keşfedin',
    rating: '4.8',
    reviews: '1.850+',
    shipping: 'Hepsipay ile Taksit',
    shippingIcon: 'ri-bank-card-line',
  },
  {
    name: 'GittiGidiyor',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Gittigidiyor_logo.png/600px-Gittigidiyor_logo.png',
    url: 'https://www.gittigidiyor.com/magaza/vitasso',
    bgGradient: 'from-teal-50 to-emerald-100/50',
    borderColor: 'border-teal-200',
    hoverBorder: 'hover:border-teal-400',
    hoverShadow: 'hover:shadow-teal-200/60',
    accentColor: 'text-teal-600',
    badgeBg: 'bg-teal-500',
    iconBg: 'bg-teal-100',
    description: 'GittiGidiyor\'da özel fırsatları kaçırmayın',
    rating: '4.7',
    reviews: '980+',
    shipping: 'Ücretsiz Kargo',
    shippingIcon: 'ri-truck-line',
  },
];

function FloatingParticle({ delay, x, y, size }: { delay: number; x: string; y: string; size: number }) {
  return (
    <div
      className="absolute rounded-full bg-teal-300/10 animate-pulse"
      style={{
        left: x,
        top: y,
        width: size,
        height: size,
        animationDelay: `${delay}s`,
        animationDuration: '4s',
      }}
    />
  );
}

function MarketplaceCard({ marketplace, index }: { marketplace: typeof marketplaces[0]; index: number }) {
  const reveal = useScrollReveal({ animation: 'zoomIn', delay: index * 200 });
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div ref={reveal.ref} className={`${reveal.className}`}>
      <a
        href={marketplace.url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="block"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className={`relative bg-gradient-to-br ${marketplace.bgGradient} rounded-3xl border-2 ${marketplace.borderColor} ${marketplace.hoverBorder} ${marketplace.hoverShadow} p-8 transition-all duration-500 cursor-pointer overflow-hidden group hover:shadow-2xl hover:-translate-y-3`}
        >
          {/* Animated background glow */}
          <div
            className={`absolute inset-0 bg-gradient-to-br ${marketplace.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-700`}
          />
          <div
            className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-white/20 blur-3xl transition-transform duration-700 group-hover:scale-150"
          />
          <div
            className="absolute -bottom-16 -left-16 w-40 h-40 rounded-full bg-white/15 blur-2xl transition-transform duration-700 group-hover:scale-125"
          />

          {/* Content */}
          <div className="relative z-10">
            {/* Logo area */}
            <div className="flex items-center justify-between mb-6">
              <div className="h-12 flex items-center">
                <img
                  src={marketplace.logo}
                  alt={`${marketplace.name} Logo`}
                  className="h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <div className={`${marketplace.badgeBg} text-white text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider whitespace-nowrap flex items-center gap-1.5 transition-transform duration-500 group-hover:scale-105`}>
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                Resmi Mağaza
              </div>
            </div>

            {/* Description */}
            <p className="text-gray-600 text-sm mb-6 leading-relaxed">
              {marketplace.description}
            </p>

            {/* Stats row */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-1.5">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-amber-400 text-xs"></i>
                  ))}
                </div>
                <span className="text-sm font-bold text-gray-800">{marketplace.rating}</span>
              </div>
              <div className="w-px h-4 bg-gray-300" />
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-chat-3-line text-gray-400 text-xs"></i>
                </div>
                <span className="text-xs text-gray-500 font-medium">{marketplace.reviews} Değerlendirme</span>
              </div>
            </div>

            {/* Shipping badge */}
            <div className={`inline-flex items-center gap-2 ${marketplace.iconBg} rounded-xl px-4 py-2.5 mb-6`}>
              <div className={`w-5 h-5 flex items-center justify-center ${marketplace.accentColor}`}>
                <i className={`${marketplace.shippingIcon} text-base`}></i>
              </div>
              <span className={`text-xs font-semibold ${marketplace.accentColor}`}>{marketplace.shipping}</span>
            </div>

            {/* CTA Button */}
            <div className={`w-full flex items-center justify-center gap-3 py-4 rounded-2xl bg-white border-2 ${marketplace.borderColor} ${marketplace.hoverBorder} transition-all duration-500 group-hover:bg-gray-900 group-hover:border-gray-900`}>
              <span className={`text-sm font-bold ${marketplace.accentColor} group-hover:text-white transition-colors duration-500`}>
                Mağazaya Git
              </span>
              <div
                className={`w-5 h-5 flex items-center justify-center ${marketplace.accentColor} group-hover:text-white transition-all duration-500`}
                style={{
                  transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
                  transition: 'transform 0.4s ease',
                }}
              >
                <i className="ri-arrow-right-line text-base"></i>
              </div>
            </div>
          </div>
        </div>
      </a>
    </div>
  );
}

function AnimatedCounter({ target }: { target: string }) {
  const [display, setDisplay] = useState('0');
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const numericPart = target.replace(/[^0-9]/g, '');
          const num = parseInt(numericPart, 10);
          if (isNaN(num)) {
            setDisplay(target);
            return;
          }
          const duration = 1800;
          const steps = 50;
          const stepTime = duration / steps;
          let current = 0;
          const increment = num / steps;
          const interval = setInterval(() => {
            current += increment;
            if (current >= num) {
              setDisplay(target);
              clearInterval(interval);
            } else {
              const prefix = target.match(/^[^0-9]*/)?.[0] ?? '';
              const suffixPart = target.match(/[^0-9]*$/)?.[0] ?? '';
              setDisplay(`${prefix}${Math.floor(current)}${suffixPart}`);
            }
          }, stepTime);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{display}</span>;
}

export default function MarketplaceSection() {
  const titleReveal = useScrollReveal({ animation: 'fadeUp', delay: 0 });
  const statsReveal = useScrollReveal({ animation: 'fadeUp', delay: 400 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const windowH = window.innerHeight;
      const start = windowH;
      const end = -rect.height;
      const progress = Math.min(1, Math.max(0, (start - rect.top) / (start - end)));
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative py-24 overflow-hidden bg-gradient-to-b from-white via-gray-50/50 to-white" ref={containerRef}>
      {/* Parallax background elements */}
      <div
        className="absolute top-10 left-[10%] w-72 h-72 bg-orange-100/20 rounded-full blur-3xl"
        style={{ transform: `translateY(${scrollProgress * -50}px) scale(${0.7 + scrollProgress * 0.5})` }}
      />
      <div
        className="absolute bottom-10 right-[10%] w-80 h-80 bg-brand-100/20 rounded-full blur-3xl"
        style={{ transform: `translateY(${scrollProgress * 40}px) scale(${0.7 + scrollProgress * 0.4})` }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-50/30 rounded-full blur-3xl"
        style={{ transform: `translate(-50%, -50%) scale(${0.6 + scrollProgress * 0.6})` }}
      />

      {/* Floating particles */}
      <FloatingParticle delay={0} x="15%" y="20%" size={8} />
      <FloatingParticle delay={1.5} x="80%" y="15%" size={6} />
      <FloatingParticle delay={0.8} x="70%" y="75%" size={10} />
      <FloatingParticle delay={2} x="25%" y="80%" size={7} />
      <FloatingParticle delay={1} x="50%" y="10%" size={5} />

      <div className="max-w-[1440px] mx-auto px-4 relative z-10">
        {/* Section Title */}
        <div ref={titleReveal.ref} className={`text-center mb-16 ${titleReveal.className}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/60 rounded-full px-6 py-2.5 mb-6">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-store-2-line text-orange-500 text-sm"></i>
            </div>
            <span className="text-orange-600 text-xs font-semibold tracking-wider uppercase">
              Resmi E-Ticaret Mağazalarımız
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-5 leading-tight">
            Türkiye&apos;nin En Büyük
            <br />
            <span className="bg-gradient-to-r from-orange-500 via-amber-500 to-brand-500 bg-clip-text text-transparent">
              Pazaryerlerinde
            </span>{' '}
            Satıştayız
          </h2>
          <p className="text-gray-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Vitasso ürünlerini güvendiğiniz e-ticaret platformlarından kolayca satın alabilirsiniz.
            Tüm mağazalarımız resmi ve garantili satış noktalarıdır.
          </p>
        </div>

        {/* Marketplace Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {marketplaces.map((marketplace, index) => (
            <MarketplaceCard key={marketplace.name} marketplace={marketplace} index={index} />
          ))}
        </div>

        {/* Stats Bar */}
        <div ref={statsReveal.ref} className={`${statsReveal.className}`}>
          <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 md:p-10 relative overflow-hidden">
            {/* Shimmer */}
            <div className="absolute inset-0 shimmer-bg opacity-30" />
            {/* Decorative lines */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-teal-400/30 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-orange-400/30 to-transparent" />

            <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1.5">
                  <AnimatedCounter target="5.200+" />
                </div>
                <div className="text-gray-400 text-xs font-medium uppercase tracking-wider">Toplam Değerlendirme</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1.5">
                  <AnimatedCounter target="50.000+" />
                </div>
                <div className="text-gray-400 text-xs font-medium uppercase tracking-wider">Satılan Ürün</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent mb-1.5">
                  <AnimatedCounter target="4.8" />
                </div>
                <div className="text-gray-400 text-xs font-medium uppercase tracking-wider">Ortalama Puan</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1.5">
                  %<AnimatedCounter target="98" />
                </div>
                <div className="text-gray-400 text-xs font-medium uppercase tracking-wider">Müşteri Memnuniyeti</div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust note */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 md:gap-10">
          {[
            { icon: 'ri-shield-check-line', text: 'Resmi Satıcı Garantisi' },
            { icon: 'ri-refund-2-line', text: 'Kolay İade' },
            { icon: 'ri-secure-payment-line', text: 'Güvenli Ödeme' },
            { icon: 'ri-customer-service-2-line', text: '7/24 Müşteri Desteği' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-gray-400 hover:text-brand-600 transition-colors duration-300 cursor-default">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-base`}></i>
              </div>
              <span className="text-xs font-medium">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
